import React, { useEffect, useState } from 'react';

import { Container, Card } from 'react-bootstrap';

import CustomNavbar from '../components/CustomNavbar';

import StatsCards from '../components/admin/StatsCards';
import LowStockCard from '../components/admin/LowStockCard';
import SalesCharts from '../components/admin/SalesCharts';
import ProductRankings from '../components/admin/ProductRankings';
import RecentActivity from '../components/admin/RecentActivity';
import ManagementCards from '../components/admin/ManagementCards';
import ManagementQuickNav from '../components/admin/ManagementQuickNav';

/* Wall-clock readout in the hero status bar, ticking once a second. */
function LiveClock() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);

    return () => clearInterval(id);
  }, []);

  return <>{now.toLocaleTimeString()}</>;
}

/* Types a line out character by character, leaving a blinking caret behind. */
function Typewriter({ text, speed = 26 }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const reduced = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    )?.matches;

    if (reduced) {
      setCount(text.length);
      return;
    }

    setCount(0);

    const id = setInterval(() => {
      setCount((n) => {
        if (n >= text.length) {
          clearInterval(id);
          return n;
        }

        return n + 1;
      });
    }, speed);

    return () => clearInterval(id);
  }, [text, speed]);

  return (
    <>
      {text.slice(0, count)}
      <span className="admin-caret" />
    </>
  );
}

/*
  Dashboard shell: renders the hero + shared styles and composes the
  self-contained analytics / table sections (each fetches its own data).
*/
function AdminDashboard() {
  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });

  return (
    <>
      <CustomNavbar />

      <div className="admin-dash">
        <Container>
          {/* Header */}
          <Card className="border-0 shadow-lg p-4 p-md-5 mb-5 admin-hero">
            <span className="admin-hero-blob admin-hero-blob-1" />
            <span className="admin-hero-blob admin-hero-blob-2" />

            {/* Control-room floor receding into the distance */}
            <span className="admin-hero-floor" aria-hidden="true" />

            {/* Monitor refresh line sweeping down the panel */}
            <span className="admin-hero-scan" aria-hidden="true" />

            <div className="position-relative">
              <div className="admin-hero-status">
                <span className="admin-chip is-live">
                  <span className="admin-chip-dot" /> System Online
                </span>

                <span className="admin-chip">
                  <span className="admin-chip-key">Today</span>
                  {today}
                </span>

                <span className="admin-chip is-mono">
                  <span className="admin-chip-key">Time</span>
                  <LiveClock />
                </span>
              </div>

              <h1 className="fw-bold mb-1 admin-hero-title">
                <span className="admin-hero-icon">👨‍💼</span> Admin Dashboard
              </h1>

              <p className="mb-0 admin-hero-text">
                <Typewriter text="Manage products, orders, users and more — all in one place." />
              </p>
            </div>
          </Card>

          {/* Quick access shortcuts — reach any section without scrolling */}
          <ManagementQuickNav />

          {/* Summary metrics */}
          <StatsCards />

          {/* Low stock alert */}
          <LowStockCard />

          {/* Charts */}
          <SalesCharts />

          {/* Product leaderboards */}
          <ProductRankings />

          {/* Recent tickets / orders / users / activity */}
          <RecentActivity />

          {/* Management navigation */}
          <ManagementCards />
        </Container>
      </div>

      <style>{adminStyles}</style>
    </>
  );
}

const adminStyles = `
  .admin-dash {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(24px, 4vw, 50px) 0;
  }

  /* ===== Header hero — a control room ===== */
  .admin-hero {
    border-radius: 25px;
    color: #fff;
    background: linear-gradient(120deg, #020617 0%, #0f172a 38%, #7c2d12 74%, #ea580c 100%);
    position: relative;
    overflow: hidden;
    box-shadow: 0 26px 60px rgba(15, 23, 42, 0.4) !important;
  }
  .admin-hero-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.45;
    pointer-events: none;
    z-index: 0;
  }
  .admin-hero-blob-1 {
    width: 220px; height: 220px;
    background: #f59e0b;
    top: -90px; left: 10%;
  }
  .admin-hero-blob-2 {
    width: 200px; height: 200px;
    background: #38bdf8;
    bottom: -100px; right: 8%;
    opacity: 0.3;
  }

  /* Perspective floor rushing toward the viewer */
  .admin-hero-floor {
    position: absolute;
    left: -30%; right: -30%; bottom: -14px;
    height: 190px;
    z-index: 0;
    pointer-events: none;
    opacity: 0.45;
    background-image:
      linear-gradient(rgba(253, 230, 138, 0.4) 1px, transparent 1px),
      linear-gradient(90deg, rgba(253, 230, 138, 0.22) 1px, transparent 1px);
    background-size: 64px 42px;
    transform: perspective(240px) rotateX(64deg);
    transform-origin: bottom center;
    mask-image: linear-gradient(180deg, transparent, #000 55%);
    -webkit-mask-image: linear-gradient(180deg, transparent, #000 55%);
    animation: adminFloor 2.6s linear infinite;
  }

  /* Refresh line travelling down the panel every few seconds */
  .admin-hero-scan {
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    overflow: hidden;
  }
  .admin-hero-scan::before {
    content: '';
    position: absolute;
    left: 0; right: 0;
    top: -2%;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(253,230,138,0.85), transparent);
    box-shadow: 0 0 22px rgba(251, 191, 36, 0.7);
    animation: adminScan 6.5s ease-in-out infinite;
  }

  /* Status chips above the heading */
  .admin-hero-status {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 16px;
  }
  .admin-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.74rem;
    font-weight: 700;
    letter-spacing: 0.6px;
    padding: 6px 14px;
    border-radius: 30px;
    color: #fff7ed;
    background: rgba(255, 255, 255, 0.09);
    border: 1px solid rgba(255, 255, 255, 0.18);
    backdrop-filter: blur(8px);
  }
  .admin-chip.is-mono {
    font-variant-numeric: tabular-nums;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    letter-spacing: 0;
  }
  .admin-chip-key {
    font-size: 0.62rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    opacity: 0.55;
  }
  .admin-chip.is-live {
    color: #86efac;
    border-color: rgba(134, 239, 172, 0.4);
    background: rgba(34, 197, 94, 0.12);
  }
  .admin-chip-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: #4ade80;
    animation: adminBlip 1.6s ease-in-out infinite;
  }

  .admin-hero-title {
    font-size: clamp(1.6rem, 4.5vw, 2.4rem);
    text-shadow: 0 4px 18px rgba(0,0,0,0.3);
  }
  /* The badge leans side to side, like scanning the room */
  .admin-hero-icon {
    display: inline-block;
    transform-origin: 50% 80%;
    animation: adminSurvey 6s ease-in-out infinite;
  }
  .admin-hero-text {
    opacity: 0.85;
    font-size: clamp(0.9rem, 2.4vw, 1.05rem);
    min-height: 1.6em;
  }
  .admin-caret {
    display: inline-block;
    width: 2px;
    height: 1em;
    margin-left: 3px;
    vertical-align: -0.15em;
    background: #fbbf24;
    animation: adminCaret 1s steps(1) infinite;
  }

  @keyframes adminFloor {
    to { background-position: 0 42px, 0 0; }
  }
  @keyframes adminScan {
    0%        { top: -2%; opacity: 0; }
    8%        { opacity: 0.9; }
    55%       { top: 102%; opacity: 0.9; }
    62%, 100% { top: 102%; opacity: 0; }
  }
  @keyframes adminBlip {
    0%, 100% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.7); }
    50%      { box-shadow: 0 0 0 6px rgba(74, 222, 128, 0); }
  }
  @keyframes adminSurvey {
    0%, 60%, 100% { transform: rotate(0deg); }
    70%           { transform: rotate(-11deg); }
    80%           { transform: rotate(9deg); }
    90%           { transform: rotate(-4deg); }
  }
  @keyframes adminCaret {
    0%, 49%   { opacity: 1; }
    50%, 100% { opacity: 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    .admin-hero-floor,
    .admin-hero-scan::before,
    .admin-hero-icon,
    .admin-chip-dot,
    .admin-caret { animation: none; }
    .admin-hero-scan { display: none; }
  }

  /* Stat cards */
  .stat-card {
    border-radius: 18px !important;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
  }
  .stat-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 16px 32px rgba(194, 65, 12, 0.18) !important;
  }
  .stat-card h1 {
    font-size: 2.1rem;
    margin-bottom: 0.35rem;
  }
  .stat-card h2,
  .stat-card h3 {
    font-weight: 800;
    margin-bottom: 0.25rem;
  }
  .stat-card p {
    margin-bottom: 0;
    font-size: 0.85rem;
  }

  /* Section cards (charts / tables / lists) */
  .admin-dash .card:not(.stat-card):not(.mgmt-card):not(.admin-hero) {
    border-radius: 20px;
  }

  /* Recent order / list rows */
  .admin-order-row {
    transition: background 0.2s ease;
  }
  .admin-order-row:hover {
    background: #f8fafc;
  }

  /* Management nav cards */
  .mgmt-card {
    border-radius: 24px !important;
    min-height: 230px !important;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }
  .mgmt-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 26px 48px rgba(234, 88, 12, 0.28) !important;
  }
  .mgmt-card h1 {
    font-size: 3rem !important;
  }
  .mgmt-card h3 {
    min-height: auto !important;
    font-size: 1.2rem;
  }
  .mgmt-card p {
    font-size: 0.95rem !important;
  }
`;

export default AdminDashboard;
